import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BrowserChromeThemeService } from '@core/browser-chrome/browser-chrome-theme.service';
import { AppSettings } from 'src/app/config';
import { CoreService } from 'src/app/services/core.service';
import { MaterialModule } from 'src/app/material.module';

@Component({
  selector: 'app-blank',
  templateUrl: './blank.component.html',
  styleUrls: [],
  imports: [RouterOutlet, MaterialModule, CommonModule],
})
export class BlankComponent {
  private readonly settings = inject(CoreService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browserChrome = inject(BrowserChromeThemeService);
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly htmlElement = this.doc.documentElement;

  options = this.settings.getOptions();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.receiveOptions(this.options);
    }
    this.syncBrowserChrome(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.syncBrowserChrome(event.urlAfterRedirects));
  }

  receiveOptions(options: AppSettings): void {
    this.toggleDarkTheme(options);
    this.toggleColorsTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }

  toggleColorsTheme(options: AppSettings) {
    const classList = this.htmlElement.classList;
    for (let index = classList.length - 1; index >= 0; index -= 1) {
      const className = classList.item(index);
      if (className?.endsWith('_theme')) {
        classList.remove(className);
      }
    }

    classList.add(options.activeTheme);
  }

  private syncBrowserChrome(url: string): void {
    const path = url.split('?')[0];
    if (
      path.startsWith('/authentication') ||
      path.startsWith('/legal') ||
      path.startsWith('/website')
    ) {
      this.browserChrome.applyAuth();
      return;
    }
    if (path.startsWith('/onboard')) {
      this.browserChrome.applyOnboard();
    }
  }
}
